import React from 'react'
import { Line } from 'react-simple-maps'

import format from '@st-operations/uikit/lib/format'

import { TIME_INTERVAL_VALUES } from '_components/interval-select/index.jsx'

import { UIKIT_COLORS } from '../../constants'
import { useLazyLoad } from '_hooks'

const monthNames = [
  'Jan.',
  'Feb.',
  'Mar.',
  'Apr.',
  'May',
  'Jun.',
  'Jul.',
  'Aug.',
  'Sept.',
  'Oct.',
  'Nov.',
  'Dec.',
]

const normalizeData = (countsByDate) => {
  if (countsByDate) {
    return Object.entries(countsByDate).reduce((acc, [key, value]) => {
      const year = parseInt(key.substring(0, 4), 10)
      const month = parseInt(key.substring(4, 6), 10)
      const day = parseInt(key.substring(6, 8), 10)

      const date = new Date(year, month - 1, day || 1)
      return [...acc, { date, value }]
    }, [])
  }

  return []
}

const TYPE_COLOR_MAPPING = {
  users: {
    primary: UIKIT_COLORS.ds_blue_dark,
    secondary: UIKIT_COLORS.ds_blue_lightest,
  },
  events: {
    primary: UIKIT_COLORS.ds_green,
    secondary: UIKIT_COLORS.ds_green_five,
  },
}

const AverageCountsPerInterval = ({ data = [], interval, type = 'events' }) => {
  if (data?.length === 0) {
    return null
  }

  const normalizedData = normalizeData(data)

  const [
    { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis },
    loadingRecharts,
  ] = useLazyLoad(import('recharts'))

  return (
    <If condition={!loadingRecharts}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={normalizedData} margin={{ top: 0, left: 0, right: 0, bottom: 50 }}>
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="50%" stopColor={TYPE_COLOR_MAPPING[type].primary} stopOpacity={1} />
              <stop
                offset="100%"
                stopColor={TYPE_COLOR_MAPPING[type].secondary}
                stopOpacity={0.95}
              />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="2 2" />
          <XAxis
            dataKey="date"
            angle={-70}
            tickFormatter={(val) =>
              [TIME_INTERVAL_VALUES.DAILY, TIME_INTERVAL_VALUES.WEEKLY].includes(interval)
                ? val.toLocaleDateString()
                : interval === TIME_INTERVAL_VALUES.MONTHLY
                ? `${monthNames[val.getMonth()]} ${val.getFullYear()}`
                : ''
            }
            textAnchor="end"
            label={{
              value: 'Date',
              position: 'bottom',
              offset: 25,
              style: { textAnchor: 'middle' },
            }}
            height={60}
          />
          <YAxis
            tickFormatter={(number) => format('metric', number)}
            label={{
              value: type === 'events' ? 'Events' : 'Users',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              style: { textAnchor: 'middle' },
            }}
            width={70}
          />
          <Tooltip
            labelFormatter={(val) => val.toLocaleDateString()}
            formatter={(val) => format('metric', val)}
          />
          <Line
            type="monotone"
            unit="M"
            strokeLinecap="round"
            strokeWidth={2}
            style={{ strokeDasharray: `40% 60%` }}
            dataKey="value"
            stroke={TYPE_COLOR_MAPPING[type].primary}
            legendType="none"
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={TYPE_COLOR_MAPPING[type].primary}
            fill="url(#colorUv)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </If>
  )
}

export default AverageCountsPerInterval
